/**
 * Upload queue for the asset library upload panel (flag assets.uploads). Framework-free so it is testable in node:
 * the panel subscribes to it (useSyncExternalStore). Per file: local pre-check (type allowlist, per-type and plan
 * size caps, remaining quota) -> POST /uploads (one Idempotency-Key per file, kept across retries, so a retry never
 * creates a second session) -> single streamed PUT, or 16 MB parts for files over 50 MB (a retry resumes from the
 * parts the server already has) -> POST /complete -> ready | pending_scan | rejected. Cancel aborts the transfer
 * and the server session. Byte progress comes from XMLHttpRequest upload events (fetch has none).
 */
import { uploadCheck, type UploadRejectReason, type UploadUsageView, type UploadView } from '@qm/contracts/asset-uploads';
import { bff, BffError } from '../../../lib/bff';

export type ItemStatus = 'queued' | 'uploading' | 'finalizing' | 'pending_scan' | 'ready' | 'error' | 'cancelled';

export interface UploadItem {
  key: string; name: string; size: number; type: string;
  status: ItemStatus; loaded: number;
  /** i18n key of the failure (deliverables.assets.upload.reason.* / .error.*) */
  errorKey: string | null; retryable: boolean;
  uploadId: string | null; assetId: string | null;
}

export interface FileLike { name: string; size: number; type: string; slice(start: number, end: number): Blob | FileLike }

export interface UploadTransport {
  create(body: { filename: string; mime: string; sizeBytes: number }, idempotencyKey: string): Promise<UploadView>;
  get(uploadId: string): Promise<UploadView>;
  put(path: `/v1/${string}`, body: Blob | FileLike, onProgress: (loaded: number) => void, signal: AbortSignal): Promise<void>;
  complete(uploadId: string): Promise<UploadView>;
  cancel(uploadId: string): Promise<void>;
}

const base = '/v1/deliverables/assets/uploads';

/** Same-origin BFF transport. PUTs use XMLHttpRequest for upload progress; JSON calls use bff(). */
export const bffTransport: UploadTransport = {
  create: (body, key) => bff<UploadView>(`${base}`, { method: 'POST', body: JSON.stringify(body), headers: { 'idempotency-key': key } }),
  get: (id) => bff<UploadView>(`${base}/${encodeURIComponent(id)}`),
  complete: (id) => bff<UploadView>(`${base}/${encodeURIComponent(id)}/complete`, { method: 'POST', body: '{}' }),
  cancel: async (id) => { await bff(`${base}/${encodeURIComponent(id)}`, { method: 'DELETE' }); },
  put: (path, body, onProgress, signal) => new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `/bff${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', 'application/octet-stream');
    xhr.setRequestHeader('accept', 'application/json');
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { resolve(); return; }
      let env = null;
      try { env = JSON.parse(xhr.responseText); } catch { /* not JSON */ }
      reject(new BffError(xhr.status, env));
    };
    xhr.onerror = () => reject(new Error('network'));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body as Blob);
  }),
};

/** i18n key for a rejection reason (API or local pre-check). */
const REASONS: readonly string[] = [
  'unsupported_type', 'extension_mismatch', 'too_large', 'plan_limit', 'quota_exceeded', 'size_mismatch', 'checksum_mismatch', 'content_mismatch',
  'executable', 'unknown_content', 'unsafe_svg', 'office_macro', 'malware_detected', 'empty_file',
];
export const uploadReasonKey = (r: string | null | undefined) => `deliverables.assets.upload.reason.${r && REASONS.includes(r) ? r : 'other'}`;

/** Map an API/network failure to { i18n key, retryable, restart (needs a new session) }. */
export function classifyUploadError(e: unknown): { key: string; retryable: boolean; restart: boolean } {
  if (e instanceof BffError) {
    const d = e.envelope?.error.details as { code?: string; message?: string }[] | { code?: string } | undefined;
    const reason = Array.isArray(d) ? d[0]?.message : undefined;
    const code = !Array.isArray(d) ? d?.code : undefined;
    if (e.envelope?.error.code === 'feature_disabled') return { key: 'deliverables.assets.upload.error.unavailable', retryable: false, restart: false };
    if (code === 'upload_expired') return { key: 'deliverables.assets.upload.error.expired', retryable: true, restart: true };
    if (code?.startsWith('upload_')) return { key: 'deliverables.assets.upload.error.generic', retryable: true, restart: true };
    if (reason) {
      // a transfer that arrived short can be sent again on the same session; content verdicts need a new file
      const retryable = reason === 'size_mismatch';
      return { key: uploadReasonKey(reason), retryable, restart: false };
    }
    if (e.status === 401 || e.status === 403) return { key: 'deliverables.assets.upload.error.forbidden', retryable: false, restart: false };
    if (e.status >= 500 || e.status === 429) return { key: 'deliverables.assets.upload.error.network', retryable: true, restart: false };
    return { key: 'deliverables.assets.upload.error.generic', retryable: true, restart: false };
  }
  return { key: 'deliverables.assets.upload.error.network', retryable: true, restart: false };
}

export const newUploadKey = () => `asset-upload-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

interface Entry { item: UploadItem; file: FileLike; idemKey: string; ctl: AbortController | null }

export class Uploader {
  private entries: Entry[] = [];
  private listeners = new Set<() => void>();
  private snap: readonly UploadItem[] = [];
  private active = 0;
  private seq = 0;

  constructor(private usage: UploadUsageView, private transport: UploadTransport = bffTransport, private opts: { concurrency?: number; onSettled?: (item: UploadItem) => void } = {}) {}

  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  snapshot = (): readonly UploadItem[] => this.snap;
  setUsage(u: UploadUsageView) { this.usage = u; }

  private emit() {
    this.snap = this.entries.map((e) => ({ ...e.item }));
    for (const l of this.listeners) l();
  }
  private patch(e: Entry, p: Partial<UploadItem>) { Object.assign(e.item, p); this.emit(); }

  /** Bytes this panel will still add (not yet reserved by the server). */
  private pendingBytes() {
    return this.entries.filter((e) => e.item.status === 'queued' && !e.item.uploadId).reduce((n, e) => n + e.item.size, 0);
  }

  add(files: Iterable<FileLike>): void {
    for (const file of files) {
      const item: UploadItem = { key: `f${++this.seq}`, name: file.name, size: file.size, type: file.type, status: 'queued', loaded: 0, errorKey: null, retryable: false, uploadId: null, assetId: null };
      const chk = uploadCheck({ filename: file.name, mime: file.type, sizeBytes: file.size }, this.usage.maxFileBytes);
      let reason: UploadRejectReason | null = chk.ok ? null : chk.reason;
      if (!reason && this.usage.usedBytes + this.usage.reservedBytes + this.pendingBytes() + file.size > this.usage.quotaBytes) reason = 'quota_exceeded';
      if (reason) Object.assign(item, { status: 'error', errorKey: uploadReasonKey(reason), retryable: false });
      this.entries.push({ item, file, idemKey: newUploadKey(), ctl: null });
    }
    this.emit();
    this.pump();
  }

  cancel(key: string): void {
    const e = this.entries.find((x) => x.item.key === key);
    if (!e || ['ready', 'pending_scan', 'cancelled'].includes(e.item.status)) return;
    e.ctl?.abort();
    const id = e.item.uploadId;
    this.patch(e, { status: 'cancelled', errorKey: null, retryable: false });
    if (id) void this.transport.cancel(id).catch(() => undefined);
  }

  retry(key: string): void {
    const e = this.entries.find((x) => x.item.key === key);
    if (!e || e.item.status !== 'error' || !e.item.retryable) return;
    this.patch(e, { status: 'queued', errorKey: null, loaded: 0 });
    this.pump();
  }

  remove(key: string): void {
    const e = this.entries.find((x) => x.item.key === key);
    if (!e || e.item.status === 'uploading' || e.item.status === 'finalizing') return;
    this.entries = this.entries.filter((x) => x !== e);
    this.emit();
  }

  /** Resolves when nothing is queued or running (tests). */
  async idle(): Promise<void> {
    while (this.active || this.entries.some((e) => e.item.status === 'queued')) await new Promise((r) => setTimeout(r, 0));
  }

  private pump() {
    const max = this.opts.concurrency ?? 2;
    while (this.active < max) {
      const next = this.entries.find((e) => e.item.status === 'queued');
      if (!next) return;
      this.active += 1;
      this.patch(next, { status: 'uploading' });
      void this.run(next).finally(() => { this.active -= 1; this.pump(); });
    }
  }

  private async run(e: Entry): Promise<void> {
    const ctl = new AbortController();
    e.ctl = ctl;
    try {
      let view: UploadView;
      if (!e.item.uploadId) {
        view = await this.transport.create({ filename: e.file.name, mime: e.file.type, sizeBytes: e.file.size }, e.idemKey);
        this.patch(e, { uploadId: view.id, assetId: view.assetId });
        // cancelled while the session was being created: close it on the server too
        if (ctl.signal.aborted) { void this.transport.cancel(view.id).catch(() => undefined); return; }
      } else {
        view = await this.transport.get(e.item.uploadId);
      }
      if (ctl.signal.aborted) return;
      if (view.mode === 'single') {
        await this.transport.put(view.uploadPath as `/v1/${string}`, e.file.slice(0, e.file.size), (n) => this.patch(e, { loaded: Math.min(n, e.item.size) }), ctl.signal);
      } else {
        const size = view.partSize!;
        const done = new Set(view.uploadedParts);
        let base = [...done].reduce((n, p) => n + Math.min(size, e.file.size - (p - 1) * size), 0);
        this.patch(e, { loaded: base });
        for (let n = 1; n <= view.partCount!; n++) {
          if (done.has(n)) continue;
          const start = (n - 1) * size;
          const end = Math.min(e.file.size, start + size);
          const at = base;
          await this.transport.put(view.uploadPath.replace('{n}', String(n)) as `/v1/${string}`, e.file.slice(start, end), (l) => this.patch(e, { loaded: at + Math.min(l, end - start) }), ctl.signal);
          base += end - start;
          this.patch(e, { loaded: base });
          if (ctl.signal.aborted) return;
        }
      }
      if (ctl.signal.aborted) return;
      this.patch(e, { status: 'finalizing', loaded: e.item.size });
      const fin = await this.transport.complete(view.id);
      if (fin.status === 'ready') this.patch(e, { status: 'ready' });
      else if (fin.status === 'pending_scan') this.patch(e, { status: 'pending_scan' });
      else this.patch(e, { status: 'error', errorKey: uploadReasonKey(fin.reason ?? (fin.status === 'quarantined' ? 'malware_detected' : null)), retryable: false });
    } catch (err) {
      if (ctl.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
      const c = classifyUploadError(err);
      if (c.restart) { e.item.uploadId = null; e.idemKey = newUploadKey(); }
      this.patch(e, { status: 'error', errorKey: c.key, retryable: c.retryable });
    } finally {
      e.ctl = null;
      if (['ready', 'pending_scan', 'error'].includes(e.item.status)) this.opts.onSettled?.({ ...e.item });
    }
  }
}
